<template>
  <div class="tab">
    <div v-if="loading">Loading...</div>
    <div v-else>
      <div class="fields" data-profile-fields>
        <div
          v-for="field in fields"
          :key="field.id"
          class="field"
          data-profile-field
          :data-size="field.size"
          :data-type="field.type"
          :data-new-line="field.newLine"
        >
          <profile-field
            :value="form[field.parentId]"
            :field="field.config"
            :error="isSubmitted && errors[field.id]"
            :editable="editable"
            @update="setFieldValue(field.parentId, $event)"
            @change="changesCounter += 1"
          />
        </div>
      </div>
      <div
        class="actions"
        :class="{
          fixed: fixFooter,
        }"
      >
        <div class="actions__wrapper">
          <c-button class="actions__reset" label="Отменить" @click="reset" />
          <c-button
            class="actions__submit"
            label="Сохранить"
            color="primary"
            :disabled="pending"
            :pending="pending"
            @click="submit()"
          />
          <c-button
            class="actions__submit actions__submit-and-close"
            label="Сохранить и закрыть"
            color="primary"
            :disabled="pending"
            :pending="pending"
            @click="submitAndClose"
          />
        </div>
        <observer class="footer-observer" @fullyVisible="fixFooter = false" @fullyHidden="fixFooter = true" />
      </div>
    </div>
  </div>
</template>

<script>
import CButton from "@/components/controls2/Button";
import ProfileField from "@/domains/profile/components/ProfileField";
import { getFieldSize } from "@/domains/profile/utils/get-field-size";
import { getFieldTypeConfig } from "@/domains/profile/constants/field-types";
import Observer from "@/components/IntersectionObserver";
import { validateFieldValue } from "@/domains/profile/utils/validate-field-value";
import { getFieldValue } from "@/domains/profile/utils/get-field-value";

import { throttle } from "@/utils/timer/throttle";
import { alignRows } from "./dom-utils";
import { formatParams, getUpdatedDictionaries } from "./utils";

export default {
  name: "profile-tab",
  components: { CButton, ProfileField, Observer },

  props: ["userId", "tab", "profileData", "editable"],

  data() {
    return {
      loading: false,
      pending: false,
      isSubmitted: false,
      form: {},
      fixFooter: false,
      changesCounter: 0,
    };
  },

  computed: {
    fieldsCollection() {
      const collection = {};
      this.tab.fields.forEach((field) => {
        collection[field.id] = field;
      });
      return collection;
    },
    fields() {
      return this.tab.fields.map((field) => {
        const { id, parentId, newLine } = field;

        return {
          id,
          parentId,
          newLine,
          size: this.getFieldSize(field),
          type: this.getFieldType(field),
          config: field,
        };
      });
    },
    errors() {
      const errors = {};
      this.tab.fields.forEach((field) => {
        const { id, parentId } = field;
        const value = this.form[parentId];
        errors[id] = this.getError(field, value);
      });
      return errors;
    },
    isValid() {
      return !this.tab.fields.some((field) => {
        const error = this.errors[field.id];
        return error;
      });
    },
  },

  watch: {
    tab() {
      this.setTabData();
    },

    profileData(profileData) {
      this.tab.fields.forEach((field) => {
        const value = profileData[field.parentId] || null;
        this.$set(this.form, field.parentId, getFieldValue(field, value));
      });
    },

    changesCounter(v) {
      this.$emit("changesCounter", v > 0);
    },
  },

  created() {
    this.setTabData();
  },

  mounted() {
    const cb = throttle(() => {
      this.setRows();
    }, 500);
    cb();
    window.addEventListener("resize", cb);
    this.onDestroy = () => window.removeEventListener("resize", cb);
  },

  beforeDestroy() {
    this.onDestroy();
  },

  methods: {
    setRows() {
      alignRows(this.$el);
    },
    setTabData() {
      this.changesCounter = 0;
      this.loading = true;
      this.isSubmitted = false;

      Object.keys(this.form).forEach((key) => {
        delete this.form[key];
      });

      this.tab.fields.forEach((field) => {
        const value = this.profileData[field.parentId] || null;
        this.$set(this.form, field.parentId, getFieldValue(field, value));
      });

      this.loading = false;

      this.$nextTick(() => {
        this.setRows();
      });
    },
    getError(field, value) {
      const { isValid, error } = validateFieldValue(field, value);
      return isValid ? null : error;
    },
    getFieldType(field) {
      const fieldConfig = getFieldTypeConfig(field);
      return fieldConfig.id;
    },
    getFieldSize(field) {
      return getFieldSize(field);
    },
    setFieldValue(fieldId, value) {
      this.$set(this.form, fieldId, value);
    },
    reset() {
      this.tab.fields.forEach((field) => {
        const { parentId } = field;
        const value = this.profileData[parentId] || null;
        this.$set(this.form, parentId, getFieldValue(field, value));
      });
    },

    async submit(cb) {
      this.isSubmitted = true;
      if (!this.isValid) return;

      this.pending = true;

      const params = this.form;

      const dictionariesToUpdate = getUpdatedDictionaries(this.fieldsCollection, params);

      const dictionaries = await this.$store.dispatch(
        "personalEmployee/saveCustomProfileVariants",
        dictionariesToUpdate,
      );

      Object.entries(dictionaries).forEach(([code, items]) => {
        const list = Object.entries(items).map(([name, id]) => ({ id, name }));
        this.$store.dispatch("profile/addDictionaryItems", { code, list });
      });

      const formattedParams = formatParams(this.fieldsCollection, params, dictionaries);

      this.$store
        .dispatch("personalEmployee/saveUserProfileData", {
          id: this.userId,
          ...formattedParams,
        })
        .then((response) => {
          this.$notify({
            group: "success-message",
            data: {
              textMessage: `Изменения в анкете сотрудника для вкладки <b>${this.tab.name}</b> успешно сохранены`,
            },
          });
          this.$emit("update", response);

          if (typeof cb === "function") cb();

          this.$nextTick(() => {
            this.changesCounter = 0;
          });
        })
        .catch(() => {
          this.$notify({
            group: "error-message",
            data: {
              textMessage: "Произошла ошибка. Попробуйте ещё раз.",
            },
          });
        })
        .finally(() => {
          this.pending = false;
        });
    },

    submitAndClose() {
      this.submit(() => {
        this.$router.push({ name: "employees-list" });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
@import "~@/assets/styles/decoration";
@import "~@/assets/styles/grid";

@mixin profileContainer {
  --profile-columns-count: 6;
  --profile-columns-gap: 16px;

  --profile-xs-field: 3;
  --profile-sm-field: 3;
  --profile-md-field: 6;
  --profile-lg-field: 6;
  --profile-xl-field: 6;
  --profile-xxl-field: 6;

  [data-profile-fields] {
    display: grid;
    grid-template-columns: repeat(var(--profile-columns-count), minmax(0, 1fr));
    column-gap: var(--profile-columns-gap);
    row-gap: 25px;
  }

  [data-profile-field] {
    --profile-field-columns: var(--profile-columns-count);
    grid-column: span var(--profile-field-columns);

    &[data-new-line] {
      grid-column: 1 / span var(--profile-field-columns);
    }

    &[data-size="xs"] {
      --profile-field-columns: var(--profile-xs-field);
    }

    &[data-size="sm"] {
      --profile-field-columns: var(--profile-sm-field);
    }

    &[data-size="md"] {
      --profile-field-columns: var(--profile-md-field);
    }

    &[data-size="lg"] {
      --profile-field-columns: var(--profile-lg-field);
    }

    &[data-size="xl"] {
      --profile-field-columns: var(--profile-xl-field);
    }

    &[data-size="xxl"] {
      --profile-field-columns: var(--profile-xxl-field);
    }

    &[data-size="full"] {
      --profile-field-columns: var(--profile-columns-count);
    }

    &[data-type="photo"] {
      grid-row: span 2;
    }

    &[data-type="photo"],
    &[data-type="gender"] {
      --profile-field-columns: var(--profile-columns-count);
    }
  }

  @include bootstrapMd {
    --profile-columns-count: 10;
    --profile-columns-gap: 30px;

    --profile-xs-field: 2;
    --profile-sm-field: 2;
    --profile-md-field: 3;
    --profile-lg-field: 4;
    --profile-xl-field: 6;

    [data-profile-field] {
      &[data-type="gender"] {
        --profile-field-columns: var(--profile-xs-field);
      }

      &[data-type="photo"] {
        --profile-field-columns: var(--profile-sm-field);
      }
    }
  }

  @include bootstrapXl {
    --profile-columns-count: 12;
  }

  @include bootstrapXxl {
    --profile-xs-field: 1;
  }
}

.tab {
  @include profileContainer();
}

.actions {
  margin-top: 16px;

  padding-top: 14px;
  padding-bottom: 14px;

  position: sticky;
  bottom: -30px;

  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &.fixed {
    background: white;
    z-index: 10;
    &:before {
      content: "";
      position: absolute;
      left: -30px;
      right: -40px;
      top: 0;
      bottom: 0;
      background: white;
      z-index: 0;
      box-shadow: 0px 0px 15px rgba(0, 21, 80, 0.3);
    }
  }

  &__submit {
    margin-left: 10px;
  }

  .footer-observer {
    bottom: -1px;
    position: absolute;
  }

  /deep/ {
    .c-button {
      position: relative;
      z-index: 1;
    }
  }

  @media screen and (max-width: 992.98px) {
    bottom: 20px;
  }
}

/deep/ {
  .c-input-textarea_floating_label_active {
    .c-input-floating-label {
      white-space: normal;
    }
  }
  .c-input-required_active {
    position: static;
    margin-top: 5px;
    .c-input-required-text {
      white-space: normal !important;
    }
  }
  .c-switcher {
    margin-bottom: 0;
  }
}

@include beforeBootstrapMd {
  .fields {
    padding-bottom: 65px;
  }
  .actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0;

    &__reset,
    &__submit {
      margin: 0;
      border: none;
      border-radius: 0;
    }

    &__reset {
      width: 50%;
      border-top: 1px solid #d3d8df;
    }
    &__submit {
      width: 50%;
    }
    &__submit-and-close {
      width: 100%;
      border-top: 1px solid white;
    }
  }
}
</style>
